import { createAsyncThunk } from '@reduxjs/toolkit'
import { instance } from '../api/api'
import { EstablishmentsType, GetEstablishmentsResponseType } from './types'

export const getEstablishments = createAsyncThunk(
  'establishments/getEstablishments',
  async (
    {
      token,
      page,
      name,
      search,
      itemsPerPage,
      city,
      size,
      address,
      description,
      endDate,
      peopleCount,
      startDate,
    }: {
      token: string
      page: number
      name?: string
      search?: string
      itemsPerPage: number
      city?: string
      size?: string
      address?: string
      description?: string
      endDate?: string
      peopleCount?: string
      startDate?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<GetEstablishmentsResponseType>(
        'establishments/with-places',
        {
          params: {
            ...(page ? { page: page } : {}),
            ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
            ...(search ? { search: search } : {}),
            ...(city ? { city: city } : {}),
            ...(size ? { size: size } : {}),
            ...(name ? { name: name } : {}),
            ...(address ? { address: address } : {}),
            ...(description ? { description: description } : {}),
            ...(peopleCount ? { peopleCount: peopleCount } : {}),
            ...(endDate ? { endDate: endDate } : {}),
            ...(startDate ? { startDate: startDate } : {}),
          },
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      if (error?.response?.status === 401) {
        // Handle 401 error (e.g., redirect to login, notify user)
        return thunkAPI.rejectWithValue('Unauthorized')
      }

      // Handle other errors
      return thunkAPI.rejectWithValue(error?.response?.data?.message || 'An error occurred.')
    }
  },
)

export const getEstablishmentFromCompany = createAsyncThunk(
  'establishments/getEstablishmentsFromCompany',
  async (
    {
      token,
      page,
      itemsPerPage,
      search,
    }: {
      token: string
      page: number
      itemsPerPage: number
      search?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<GetEstablishmentsResponseType>('establishments', {
        params: {
          ...(page ? { page: page } : {}),
          ...(itemsPerPage ? { itemsPerPage: itemsPerPage } : {}),
          ...(search ? { search: search } : {}),
        },
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const getCurrentEstablishment = createAsyncThunk(
  'establishments/getCurrentEstablishment',
  async (
    { establishmentId, token }: { establishmentId: string | undefined; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.get<EstablishmentsType>(`establishments/${establishmentId}`, {
        headers: { authorization: `Bearer ${token}` },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const createEstablishment = createAsyncThunk(
  'establishments/createEstablishment',
  async ({ formData, token }: { formData: any; token: string }, thunkAPI) => {
    try {
      const response = await instance.post<any>('establishments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${token}`,
        },
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const deleteCurrentFileFromEstablishment = createAsyncThunk(
  'establishments/deleteCurrentFileFromEstablishment',
  async (
    {
      establishmentId,
      filesToDelete,
      token,
    }: { establishmentId: string | undefined; filesToDelete: string[]; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.delete<EstablishmentsType>(
        `establishments/${establishmentId}/files`,
        {
          data: { filesToDelete },
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const pushNewFilesToEstablishment = createAsyncThunk(
  'establishments/pushNewImagesToEstablishment',
  async (
    {
      establishmentId,
      formData,
      token,
    }: { establishmentId: string | undefined; formData: any; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.put<EstablishmentsType>(
        `establishments/${establishmentId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data; charset=utf-8',
            authorization: `Bearer ${token}`,
          },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.response.data.message)
    }
  },
)

export const changeEstablishmentMainInformation = createAsyncThunk(
  'establishments/changeEstablishmentMainInformation',
  async (
    {
      token,
      establishmentId,
      address,
      name,
      size,
      city,
      rentCost,
      description,
    }: {
      establishmentId: string | number
      name: string
      city: string
      size: string
      address: string
      token: string
      rentCost?: string | number
      description?: string
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<EstablishmentsType>(
        `establishments/${establishmentId}`,
        {
          address,
          name,
          size,
          description,
          city,
          rentCost,
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const changeEstablishmentDescription = createAsyncThunk(
  'establishments/changeEstablishmentDescription',
  async (
    {
      establishmentId,
      description,
      token,
    }: { establishmentId: string | number | undefined; description: string; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<EstablishmentsType>(
        `establishments/${establishmentId}`,
        {
          description,
        },
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const deleteCurrentEstablishment = createAsyncThunk(
  'establishments/deleteCurrentEstablishment',
  async (
    { establishmentId, token }: { establishmentId: string | undefined; token: string },
    thunkAPI,
  ) => {
    try {
      const response = await instance.delete<EstablishmentsType>(
        `establishments/${establishmentId}`,
        {
          headers: { authorization: `Bearer ${token}` },
        },
      )

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)

export const blockEstablishment = createAsyncThunk(
  'establishments/blockEstablishment',
  async (
    {
      establishmentId,
      isBlocked,
    }: {
      establishmentId: string | number | undefined
      isBlocked: boolean
    },
    thunkAPI,
  ) => {
    try {
      const response = await instance.patch<any>(`establishments/${establishmentId}`, {
        isBlocked,
      })

      return response.data
    } catch (error: any) {
      return thunkAPI.rejectWithValue({
        errors: [error.message],
        fieldsErrors: undefined,
      })
    }
  },
)
